import { Link, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  ChevronDown,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Settings,
  X,
} from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { AgentsContextProvider } from "@/components/Agents/AgentsContext";
import { AgentsDocumentIcons } from "@/components/Agents/AgentsDocumentIcons";
import { AgentsOrgPanel } from "@/components/Agents/AgentsOrgPanel";
import { AgentsUtilityBar } from "@/components/Agents/AgentsUtilityBar";
import { AtwWorkforceLockup } from "@/components/Brand/AtwProductMark";
import { ProfileSettingsDialog } from "@/components/Dashboard/ProfileSettingsDialog";
import {
  AGENTS_APP_BY_SLUG,
  TOP_NAV_APP_ORDER,
  appIcon,
  moduleNavActive,
  resolveAgentsApp,
  topNavAppActive,
  type AgentsApp,
  type AgentsModule,
} from "@/lib/agentsAppRegistry";
import {
  AGENT_EMAIL_SETTINGS_PANES,
  AGENT_PAGE_NAV_ITEMS,
  AGENT_SETTINGS_NAV_ITEMS,
  agentSectionPath,
  parseAgentWorkspacePath,
} from "@/lib/agentNav";
import { apiGet } from "@/lib/apiClient";
import { getAtwAgentsReleaseVersion } from "@/lib/appVersion";
import { agentsHref } from "@/lib/atwAgentsHost";
import { cn } from "@/lib/utils";

const RAIL_EXPANDED = 256;
const RAIL_COLLAPSED = 72;
const COLLAPSE_KEY = "agents-sidebar-collapsed";

function maxTopNavLinksForViewport(width: number): number {
  if (width < 640) return 2;
  if (width < 768) return 3;
  if (width < 1024) return 4;
  if (width < 1280) return 5;
  return 6;
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

function moduleLinkClass(
  active: boolean,
  collapsed: boolean,
  indented: false | "settings" | "nested" = false,
) {
  const pad =
    collapsed
      ? "justify-center gap-0 border-l-0 px-2"
      : indented === "nested"
        ? "pl-10 pr-2.5"
        : indented === "settings"
          ? "pl-6 pr-2.5"
          : "pl-3 pr-2.5";
  return cn(
    "relative z-[1] flex w-full cursor-pointer items-center gap-3.5 rounded-md border-l-[3px] py-2 text-left text-[13px] font-medium leading-snug transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111829]",
    "[&_svg]:size-4 [&_svg]:shrink-0",
    pad,
    active
      ? "border-sky-400 bg-slate-800 text-white shadow-sm [&_svg]:text-sky-200"
      : "border-transparent text-slate-300 hover:bg-slate-800/80 hover:text-white [&_svg]:text-slate-500",
  );
}

function areaLinkClass(active: boolean) {
  return cn(
    "inline-flex shrink-0 items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold whitespace-nowrap",
    active
      ? "bg-gray-100 text-indigo-600 dark:bg-white/10 dark:text-white"
      : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10",
  );
}

function userInitials(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
  }
  return label.slice(0, 2).toUpperCase();
}

function SidebarRail({
  pathname,
  activeApp,
  modules,
  collapsed,
  showCollapseToggle,
  onToggleCollapsed,
  onNavigate,
}: {
  pathname: string;
  activeApp: AgentsApp;
  modules: AgentsModule[];
  collapsed: boolean;
  showCollapseToggle?: boolean;
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
}) {
  const settingsActive = pathname === "/settings" || pathname.startsWith("/settings/");
  const settingsLabel =
    activeApp.slug === "home" ? "Home Settings" : `${activeApp.label} Settings`;
  const versionLine = `ATW Workforce · V${getAtwAgentsReleaseVersion()}`;
  const agentWorkspace = parseAgentWorkspacePath(pathname);

  return (
    <div
      data-agents-rail
      className={cn(
        "flex min-h-full min-w-0 grow flex-col gap-y-4 overflow-y-auto overflow-x-hidden border-r border-slate-800/90 bg-[#111829] py-5 shadow-md ring-1 ring-black/5",
        collapsed ? "px-2" : "px-3",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center justify-center overflow-hidden",
          collapsed ? "h-14 px-0.5" : "h-20 px-1",
        )}
      >
        {collapsed ? (
          <img
            src="/images/agents/network.png"
            alt="ATW Workforce"
            className="h-10 w-10 object-contain object-center"
            loading="eager"
            decoding="async"
          />
        ) : (
          <AtwWorkforceLockup className="h-full w-full" />
        )}
      </div>
      <nav className="flex min-w-0 flex-1 flex-col" aria-label={activeApp.label}>
        <ul role="list" className="flex flex-1 flex-col gap-y-6">
          <li>
            {collapsed ? null : (
              <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                {activeApp.label}
              </p>
            )}
            <ul className="space-y-px">
              {modules.map((item) => {
                const Icon = item.icon;
                const active =
                  item.href === "/agents" && agentWorkspace
                    ? false
                    : moduleNavActive(pathname, item.href);
                return (
                  <li key={item.href}>
                    <Link
                      to={agentsHref(item.href)}
                      className={moduleLinkClass(active, collapsed)}
                      title={collapsed ? item.name : undefined}
                      aria-current={active ? "page" : undefined}
                      onClick={onNavigate}
                    >
                      <Icon aria-hidden />
                      {collapsed ? (
                        <span className="sr-only">{item.name}</span>
                      ) : (
                        <span className="min-w-0 truncate">{item.name}</span>
                      )}
                    </Link>
                  </li>
                );
              })}
              {agentWorkspace
                ? AGENT_PAGE_NAV_ITEMS.map((item) => {
                    const Icon = item.icon;
                    const href = agentSectionPath(agentWorkspace.agentId, item.id);
                    const active = agentWorkspace.section === item.id;
                    return (
                      <li key={item.id}>
                        <Link
                          to={agentsHref(href)}
                          className={moduleLinkClass(active, collapsed, "settings")}
                          title={collapsed ? item.name : undefined}
                          aria-current={active ? "page" : undefined}
                          onClick={onNavigate}
                        >
                          <Icon aria-hidden />
                          {collapsed ? (
                            <span className="sr-only">{item.name}</span>
                          ) : (
                            <span className="min-w-0 truncate">{item.name}</span>
                          )}
                        </Link>
                      </li>
                    );
                  })
                : null}
              {agentWorkspace && !collapsed ? (
                <li className="pt-2">
                  <p
                    className={cn(
                      "px-3 text-[10px] font-semibold uppercase tracking-[0.14em]",
                      AGENT_SETTINGS_NAV_ITEMS.some((item) => item.id === agentWorkspace.section)
                        ? "text-sky-300"
                        : "text-slate-500",
                    )}
                  >
                    Settings
                  </p>
                </li>
              ) : null}
              {agentWorkspace
                ? AGENT_SETTINGS_NAV_ITEMS.flatMap((item) => {
                    const Icon = item.icon;
                    if (item.id === "email") {
                      const emailActive = agentWorkspace.section === "email";
                      const personalHref = agentSectionPath(
                        agentWorkspace.agentId,
                        "email",
                        "personal",
                      );
                      const nodes = [
                        <li key="email">
                          <Link
                            to={agentsHref(personalHref)}
                            className={moduleLinkClass(
                              collapsed ? emailActive : false,
                              collapsed,
                              "settings",
                            )}
                            title={collapsed ? item.name : undefined}
                            aria-current={collapsed && emailActive ? "page" : undefined}
                            onClick={onNavigate}
                          >
                            <Icon aria-hidden />
                            {collapsed ? (
                              <span className="sr-only">{item.name}</span>
                            ) : (
                              <span
                                className={cn(
                                  "min-w-0 truncate",
                                  emailActive ? "text-sky-200" : undefined,
                                )}
                              >
                                {item.name}
                              </span>
                            )}
                          </Link>
                        </li>,
                      ];
                      if (!collapsed) {
                        for (const pane of AGENT_EMAIL_SETTINGS_PANES) {
                          const href = agentSectionPath(
                            agentWorkspace.agentId,
                            "email",
                            pane.id,
                          );
                          const active =
                            agentWorkspace.section === "email" &&
                            agentWorkspace.emailPane === pane.id;
                          nodes.push(
                            <li key={`email-${pane.id}`}>
                              <Link
                                to={agentsHref(href)}
                                className={moduleLinkClass(active, collapsed, "nested")}
                                aria-current={active ? "page" : undefined}
                                onClick={onNavigate}
                              >
                                <span className="min-w-0 truncate">{pane.name}</span>
                              </Link>
                            </li>,
                          );
                        }
                      }
                      return nodes;
                    }
                    const href = agentSectionPath(agentWorkspace.agentId, item.id);
                    const active = agentWorkspace.section === item.id;
                    return [
                      <li key={item.id}>
                        <Link
                          to={agentsHref(href)}
                          className={moduleLinkClass(active, collapsed, "settings")}
                          title={collapsed ? item.name : undefined}
                          aria-current={active ? "page" : undefined}
                          onClick={onNavigate}
                        >
                          <Icon aria-hidden />
                          {collapsed ? (
                            <span className="sr-only">{item.name}</span>
                          ) : (
                            <span className="min-w-0 truncate">{item.name}</span>
                          )}
                        </Link>
                      </li>,
                    ];
                  })
                : null}
            </ul>
          </li>
          <li className="mt-auto border-t border-slate-700/80 pt-3">
            <div
              className={cn(
                "px-1 py-1",
                collapsed ? "flex flex-col items-center gap-1" : "flex items-center gap-1",
              )}
            >
              <Link
                to={agentsHref("/settings")}
                className={cn(
                  moduleLinkClass(settingsActive, collapsed),
                  collapsed ? "w-full" : "min-w-0 flex-1",
                )}
                title={collapsed ? settingsLabel : undefined}
                aria-current={settingsActive ? "page" : undefined}
                onClick={onNavigate}
              >
                <Settings aria-hidden className="size-4 shrink-0" />
                {collapsed ? (
                  <span className="sr-only">{settingsLabel}</span>
                ) : (
                  <span className="min-w-0 truncate">{settingsLabel}</span>
                )}
              </Link>
              {showCollapseToggle && onToggleCollapsed ? (
                <button
                  type="button"
                  onClick={onToggleCollapsed}
                  className="inline-flex shrink-0 items-center justify-center rounded-md p-1.5 text-slate-300 transition-colors hover:bg-slate-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/80"
                  aria-label={collapsed ? "Expand sidebar" : "Retract sidebar"}
                >
                  {collapsed ? (
                    <PanelLeftOpen className="size-4 shrink-0" aria-hidden />
                  ) : (
                    <PanelLeftClose className="size-4 shrink-0" aria-hidden />
                  )}
                </button>
              ) : null}
            </div>
            {collapsed ? null : (
              <p
                className="mt-2 truncate px-1 text-center text-[10px] tracking-wide text-slate-400"
                title={versionLine}
              >
                {versionLine}
              </p>
            )}
          </li>
        </ul>
      </nav>
    </div>
  );
}

function ShellChrome({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const session = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [orgOpen, setOrgOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [topNavVisibleCount, setTopNavVisibleCount] = useState(6);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement | null>(null);
  const [userProfile, setUserProfile] = useState<{
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    profilePhoto?: string | null;
  } | null>(null);

  const activeApp = useMemo(() => resolveAgentsApp(pathname), [pathname]);
  const sidebarModules = activeApp.modules;

  const topNavApps = useMemo(
    () =>
      TOP_NAV_APP_ORDER.map((slug) => {
        const app = AGENTS_APP_BY_SLUG[slug];
        return {
          slug,
          label: app.label,
          topNavHref: app.topNavHref,
          icon: appIcon(slug),
        };
      }),
    [],
  );

  const sidebarWidth = collapsed ? RAIL_COLLAPSED : RAIL_EXPANDED;
  const profileName = [userProfile?.firstName, userProfile?.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  const displayName =
    profileName ||
    userProfile?.name?.trim() ||
    session.data?.user?.name?.trim() ||
    session.data?.user?.email?.split("@")[0] ||
    "User";
  const avatarSrc = userProfile?.profilePhoto || null;

  useEffect(() => {
    if (session.status !== "authenticated") {
      setUserProfile(null);
      return;
    }
    const user = session.data?.user;
    if (!user?.email) return;
    const userId = user.id || user.email;
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{
        name?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        profilePhoto?: string | null;
      }>(`/api/users/${encodeURIComponent(userId)}`);
      if (cancelled || !res.ok || !res.data) return;
      setUserProfile({
        name: res.data.name,
        firstName: res.data.firstName,
        lastName: res.data.lastName,
        profilePhoto: res.data.profilePhoto,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [session.status, session.data?.user]);

  const activeTopNavSlug = activeApp.slug;

  const { pinnedApps, overflowApps } = useMemo(() => {
    const visibleCount = Math.min(topNavVisibleCount, topNavApps.length);
    const initialPinned = topNavApps.slice(0, visibleCount);
    if (!activeTopNavSlug || visibleCount === 0 || visibleCount === topNavApps.length) {
      return { pinnedApps: initialPinned, overflowApps: topNavApps.slice(visibleCount) };
    }
    if (initialPinned.some((app) => app.slug === activeTopNavSlug)) {
      return { pinnedApps: initialPinned, overflowApps: topNavApps.slice(visibleCount) };
    }
    const activeNav = topNavApps.find((app) => app.slug === activeTopNavSlug);
    if (!activeNav) {
      return { pinnedApps: initialPinned, overflowApps: topNavApps.slice(visibleCount) };
    }
    const promoted = [...initialPinned.slice(0, -1), activeNav];
    const promotedSlugs = new Set(promoted.map((app) => app.slug));
    return {
      pinnedApps: promoted,
      overflowApps: topNavApps.filter((app) => !promotedSlugs.has(app.slug)),
    };
  }, [topNavVisibleCount, activeTopNavSlug, topNavApps]);

  const overflowActive = overflowApps.some((app) => topNavAppActive(pathname, app.slug));

  useEffect(() => {
    setMobileOpen(false);
    setOverflowOpen(false);
  }, [pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "true" : "false");
    } catch {
      /* ignore storage errors */
    }
  }, [collapsed]);

  useEffect(() => {
    const updateCount = () => setTopNavVisibleCount(maxTopNavLinksForViewport(window.innerWidth));
    updateCount();
    window.addEventListener("resize", updateCount);
    return () => window.removeEventListener("resize", updateCount);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!target || overflowRef.current?.contains(target)) return;
      setOverflowOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, []);

  const onSearch = useCallback(
    (query: string) => {
      const q = query.trim().toLowerCase();
      if (!q) return;
      const appHit = topNavApps.find((app) => app.label.toLowerCase().includes(q));
      if (appHit) {
        navigate(agentsHref(appHit.topNavHref));
        return;
      }
      for (const slug of TOP_NAV_APP_ORDER) {
        const moduleHit = AGENTS_APP_BY_SLUG[slug].modules.find((item) =>
          item.name.toLowerCase().includes(q),
        );
        if (moduleHit) {
          navigate(agentsHref(moduleHit.href));
          return;
        }
      }
    },
    [navigate, topNavApps],
  );

  return (
    <div
      className="min-h-screen dark:bg-zinc-950"
      style={{ ["--agents-rail-w" as string]: `${sidebarWidth}px` }}
    >
      {mobileOpen ? (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/80"
            aria-label="Close sidebar backdrop"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative flex h-full max-w-xs">
            <div className="w-full max-w-xs bg-[#111829]">
              <SidebarRail
                pathname={pathname}
                activeApp={activeApp}
                modules={sidebarModules}
                collapsed={false}
                onNavigate={() => setMobileOpen(false)}
              />
            </div>
            <button
              type="button"
              className="absolute left-full top-4 ml-2 rounded-full bg-slate-800 p-2 text-white"
              onClick={() => setMobileOpen(false)}
              aria-label="Close sidebar"
            >
              <X className="size-5" />
            </button>
          </div>
        </div>
      ) : null}

      <div
        className="relative hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:flex-col"
        style={{ width: sidebarWidth }}
      >
        <SidebarRail
          pathname={pathname}
          activeApp={activeApp}
          modules={sidebarModules}
          collapsed={collapsed}
          showCollapseToggle
          onToggleCollapsed={() => setCollapsed((value) => !value)}
        />
      </div>

      <div className="bg-white pb-10 dark:bg-zinc-950 xl:pl-[var(--agents-rail-w)]">
        <div className="shadow-xs sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 dark:border-white/5 dark:bg-gray-900 dark:shadow-none sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="-m-2.5 p-2.5 text-gray-900 dark:text-white xl:hidden"
            aria-label="Open sidebar"
          >
            <Menu className="size-5" />
          </button>

          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-visible" aria-label="Apps">
            {pinnedApps.map((app) => {
              const active = topNavAppActive(pathname, app.slug);
              const Icon = app.icon;
              const showIcon = app.slug !== "home";
              return (
                <Link
                  key={app.slug}
                  to={agentsHref(app.topNavHref)}
                  className={areaLinkClass(active)}
                >
                  {showIcon ? <Icon aria-hidden className="size-4 shrink-0" /> : null}
                  {app.label}
                </Link>
              );
            })}
            {overflowApps.length > 0 ? (
              <div ref={overflowRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setOverflowOpen((open) => !open)}
                  className={areaLinkClass(overflowActive)}
                  aria-label="Show more apps"
                  aria-haspopup="menu"
                  aria-expanded={overflowOpen}
                >
                  <ChevronDown className="size-4" />
                </button>
                {overflowOpen ? (
                  <div
                    role="menu"
                    className="absolute left-0 top-full z-50 mt-2 w-52 origin-top-left rounded-md border border-gray-200 bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none dark:border-white/10 dark:bg-gray-900 dark:ring-white/10"
                  >
                    {overflowApps.map((app) => {
                      const active = topNavAppActive(pathname, app.slug);
                      const Icon = app.icon;
                      return (
                        <Link
                          key={app.slug}
                          to={agentsHref(app.topNavHref)}
                          role="menuitem"
                          className={cn(
                            "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold",
                            active
                              ? "bg-gray-100 text-indigo-600 dark:bg-white/10 dark:text-white"
                              : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10",
                          )}
                          onClick={() => setOverflowOpen(false)}
                        >
                          <Icon aria-hidden className="size-4 shrink-0" />
                          {app.label}
                        </Link>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}
          </nav>

          <form
            className="hidden md:block"
            onSubmit={(event) => {
              event.preventDefault();
              const data = new FormData(event.currentTarget);
              onSearch(String(data.get("q") || ""));
            }}
          >
            <label className="relative block w-64">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="search"
                name="q"
                placeholder="Search"
                aria-label="Search"
                className="w-full rounded-md border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none ring-indigo-500 placeholder:text-gray-400 focus:ring-2 dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </label>
          </form>

          <button
            type="button"
            onClick={() => setOrgOpen(true)}
            className="rounded-full p-0.5 text-gray-600 ring-2 ring-transparent hover:ring-indigo-200 dark:text-gray-300 dark:hover:ring-white/20"
            aria-label="Organization and account menu"
          >
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt=""
                className="size-9 rounded-full object-cover"
              />
            ) : (
              <span className="flex size-9 items-center justify-center rounded-full bg-indigo-600 text-xs font-semibold text-white">
                {userInitials(displayName)}
              </span>
            )}
          </button>
        </div>

        <main className="flex min-h-0 flex-1 flex-col bg-slate-100 p-3 sm:p-5 dark:bg-zinc-950">
          <div className="mx-auto flex min-h-full w-full max-w-[1920px] flex-1 flex-col overflow-x-auto rounded-xl border border-slate-200/90 bg-white shadow-sm dark:border-white/10 dark:bg-zinc-900">
            <div className="flex min-h-0 flex-1 flex-col p-4 sm:p-6">{children}</div>
          </div>
        </main>
      </div>

      <AgentsUtilityBar />
      <AgentsOrgPanel
        open={orgOpen}
        onOpenChange={setOrgOpen}
        userLabel={displayName}
        userImageSrc={avatarSrc}
        onOpenProfile={() => setProfileDialogOpen(true)}
      />
      <ProfileSettingsDialog
        open={profileDialogOpen}
        onOpenChange={setProfileDialogOpen}
        onProfileUpdate={() => {
          const user = session.data?.user;
          if (!user?.email) return;
          const userId = user.id || user.email;
          void (async () => {
            const res = await apiGet<{
              name?: string | null;
              firstName?: string | null;
              lastName?: string | null;
              profilePhoto?: string | null;
            }>(`/api/users/${encodeURIComponent(userId)}`);
            if (!res.ok || !res.data) return;
            setUserProfile({
              name: res.data.name,
              firstName: res.data.firstName,
              lastName: res.data.lastName,
              profilePhoto: res.data.profilePhoto,
            });
          })();
        }}
      />
    </div>
  );
}

export function AgentsShell({ children }: { children: ReactNode }) {
  return (
    <AgentsContextProvider>
      <AgentsDocumentIcons />
      <ShellChrome>{children}</ShellChrome>
    </AgentsContextProvider>
  );
}
